import { useCheckRole } from '@ospace/auth'
import { userTypes } from '@ospace/core-auth'
import { PageLink, PageTitle } from '@ospace/layout'
import { ErrorBoundary } from '@ospace/shared'
import {
  Accordion,
  Alert,
  Anchor,
  Code,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { Link, useParams } from 'react-router-dom'

import { TraceTimeline } from '../components/TraceTimeline'
import { HubspotExecutionPanel } from '../components/HubspotExecutionPanel'
import { MergeDecisionSummary } from '../components/MergeDecisionSummary'
import { ActionBadge, TraceStatusBadge } from '../components/StatusBadge'
import { useDeduplicationKindConfig } from '../hooks/useDeduplicationKind'
import { useDeduplicationTrace } from '../hooks/useDeduplicationTrace'
import { formatDateTime } from '../utils/format'
import {
  extractTerminalExecutionResult,
  isHubspotFailureEvent,
} from '../utils/hubspot-event-details'
import { groupDetailPath, runDetailPath } from '../utils/paths'
import { sortTraceStepsByExecution } from '../utils/sort-trace-steps'
import { contactMergeOutcomeLabel, mergePlanFromTrace } from '../utils/merge-plan'

const EXECUTE_EVENTS = new Set([
  'company_merged',
  'company_deleted',
  'company_domain_updated',
  'contact_merged',
  'contact_kept',
  'contact_deleted',
  'review_task_created',
  'hubspot_batch_flushed',
  'hubspot_completed',
  'hubspot_failed',
])

const TraceDetailPage = () => {
  const kindConfig = useDeduplicationKindConfig()
  const {
    runId = '',
    groupTraceId,
    traceId,
  } = useParams<{
    runId: string
    groupTraceId?: string
    traceId?: string
  }>()
  const resolvedGroupTraceId = groupTraceId ?? traceId ?? ''
  const { data: trace, isLoading, status } = useDeduplicationTrace(runId, resolvedGroupTraceId)

  const breadcrumbs: PageLink[] = [
    { title: kindConfig.title, path: kindConfig.basePath, isActive: false },
    {
      title: `Run ${runId.slice(0, 8)}…`,
      path: runDetailPath(kindConfig.kind, runId),
      isActive: false,
    },
    {
      title: trace?.companyName ?? `Group ${resolvedGroupTraceId.slice(0, 8)}…`,
      path: groupDetailPath(kindConfig.kind, runId, resolvedGroupTraceId),
      isActive: true,
    },
  ]

  if (isLoading) {
    return (
      <Group justify='center' py='xl'>
        <Loader />
      </Group>
    )
  }

  if (status === 'error' || !trace) {
    return <Text c='red'>Group not found.</Text>
  }

  const orderedSteps = sortTraceStepsByExecution(trace.steps)
  const hubspotStep = [...orderedSteps]
    .reverse()
    .find(
      (step) =>
        step.phase === 'execute' &&
        (EXECUTE_EVENTS.has(step.event) || isHubspotFailureEvent(step.event, step.metadata))
    )

  const terminal = trace.terminal ?? trace.decidedAction
  const gates = trace.gates ?? []
  const discoverySource =
    trace.discoverySource ??
    (typeof trace.plan?.discoverySource === 'string' ? trace.plan.discoverySource : null)
  const discoveryLabel =
    discoverySource === 'linkedin'
      ? 'Same LinkedIn'
      : discoverySource === 'name'
        ? 'Same name'
        : null
  const terminalExecution = extractTerminalExecutionResult(orderedSteps, terminal)
  const finalObjectId = trace.finalCompanyId ?? null
  const isMergeTerminal = terminal === 'MERGE' || trace.plan?.terminal === 'MERGE'
  const mergePlan = mergePlanFromTrace({ ...trace, steps: orderedSteps })
  const hasMergeExecution = orderedSteps.some(
    (step) => step.event === 'contact_merged' || step.event === 'company_merged'
  )
  const mergePending = isMergeTerminal && trace.status === 'in_progress' && !hasMergeExecution
  const mergeFailed = isMergeTerminal && trace.status === 'failed' && !hasMergeExecution

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>Group detail</PageTitle>

      <Anchor
        component={Link}
        to={runDetailPath(kindConfig.kind, runId)}
        mb='md'
        display='inline-block'
      >
        ← Back to run
      </Anchor>

      {trace.dryRun && (
        <Alert color='orange' mb='md' title='Dry run'>
          HubSpot actions were not executed. Merge decisions were recorded for observability only.
        </Alert>
      )}

      {!trace.dryRun && mergeFailed && (
        <Alert color='red' mb='md' title='Merge failed'>
          A merge was decided but HubSpot did not confirm execution (run may have timed out or
          errored before the merge step).
        </Alert>
      )}

      {!trace.dryRun && mergePending && (
        <Alert color='yellow' mb='md' title='Merge pending'>
          A merge was decided but no HubSpot merge event has been recorded yet.
        </Alert>
      )}

      <Paper withBorder p='lg' mb='lg'>
        <Group justify='space-between' mb='md' wrap='wrap'>
          <Stack gap={4}>
            <Text fw={700} size='xl'>
              {trace.companyName}
            </Text>
            <Text c='dimmed' size='sm'>
              {trace.companyCount ?? '—'} {kindConfig.entityCountLabel.toLowerCase()}
              {finalObjectId ? ` · final ${finalObjectId}` : ''}
            </Text>
          </Stack>
          <Group>
            {terminal && <ActionBadge action={terminal} />}
            <TraceStatusBadge status={trace.status} />
          </Group>
        </Group>

        <Stack gap='xs'>
          {discoveryLabel && (
            <Text size='sm'>
              <Text span fw={600}>
                Discovery:{' '}
              </Text>
              {discoveryLabel}
            </Text>
          )}
          {mergePlan && (
            <Text size='sm'>
              <Text span fw={600}>
                Merge target:{' '}
              </Text>
              {mergePlan.targetContactId}
              {mergePlan.mergeContactId ? ` · merge ${mergePlan.mergeContactId}` : ''}
            </Text>
          )}
          {(mergePlan?.reason ?? trace.terminalReason) && (
            <Text size='sm'>
              <Text span fw={600}>
                Reason:{' '}
              </Text>
              {mergePlan?.reason ?? trace.terminalReason}
            </Text>
          )}
          {gates.length > 0 && (
            <Text size='sm'>
              <Text span fw={600}>
                Gates:{' '}
              </Text>
              {gates.join(', ')}
            </Text>
          )}
          {trace.summary && (
            <Text size='sm'>
              <Text span fw={600}>
                Summary:{' '}
              </Text>
              {trace.summary}
            </Text>
          )}
          {terminalExecution?.details.hubspotRecordUrl && (
            <Text size='sm'>
              <Text span fw={600}>
                Result:{' '}
              </Text>
              <Anchor
                href={terminalExecution.details.hubspotRecordUrl}
                target='_blank'
                rel='noopener noreferrer'
                size='sm'
              >
                {terminalExecution.details.hubspotRecordId
                  ? `Open in HubSpot (${terminalExecution.details.hubspotRecordId})`
                  : 'Open in HubSpot'}
              </Anchor>
            </Text>
          )}
          {terminalExecution?.details.response != null && (
            <Accordion variant='contained' chevronPosition='left'>
              <Accordion.Item value='hubspot-response'>
                <Accordion.Control>HubSpot response</Accordion.Control>
                <Accordion.Panel>
                  <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                    {JSON.stringify(terminalExecution.details.response, null, 2)}
                  </Code>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )}
          <Text size='xs' c='dimmed'>
            Started {formatDateTime(trace.startedAt)}
            {trace.completedAt ? ` · Completed ${formatDateTime(trace.completedAt)}` : ''}
          </Text>
        </Stack>
      </Paper>

      {(mergePlan || isMergeTerminal) && kindConfig.kind === 'contact' && (
        <Paper withBorder p='lg' mb='lg'>
          <Text fw={600} mb='sm'>
            Merge decision
          </Text>
          {mergePlan ? (
            <MergeDecisionSummary plan={mergePlan} />
          ) : (
            <Text size='sm' c='dimmed'>
              Merge approved; target details are not recorded for this group yet.
            </Text>
          )}
        </Paper>
      )}

      {trace.plan && Object.keys(trace.plan).length > 0 && !mergePlan && !isMergeTerminal && (
        <Paper withBorder p='lg' mb='lg'>
          <Text fw={600} mb='sm'>
            Decision plan
          </Text>
          <Accordion variant='contained' chevronPosition='left'>
            <Accordion.Item value='plan'>
              <Accordion.Control>View plan JSON</Accordion.Control>
              <Accordion.Panel>
                <Code block style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                  {JSON.stringify(trace.plan, null, 2)}
                </Code>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Paper>
      )}

      {trace.companies && trace.companies.length > 0 && (
        <Paper withBorder p='lg' mb='lg'>
          <Text fw={600} mb='sm'>
            {kindConfig.entityCountLabel}
          </Text>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>HubSpot ID</Table.Th>
                <Table.Th>{kindConfig.secondaryFieldLabel}</Table.Th>
                <Table.Th>Score</Table.Th>
                <Table.Th>Outcome</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {trace.companies.map((company) => {
                const mergeOutcome = contactMergeOutcomeLabel(company.companyId, mergePlan)
                return (
                  <Table.Tr key={company.companyId}>
                    <Table.Td>{company.companyId}</Table.Td>
                    <Table.Td>{company.domain ?? '—'}</Table.Td>
                    <Table.Td>{company.classification ?? '—'}</Table.Td>
                    <Table.Td>
                      {mergeOutcome ?? company.outcomeDetail ?? company.outcome ?? '—'}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {hubspotStep && kindConfig.kind === 'company' && <HubspotExecutionPanel step={hubspotStep} />}

      <Text fw={600} mb='md'>
        Event timeline ({orderedSteps.length} events)
      </Text>
      <TraceTimeline steps={orderedSteps} />
    </>
  )
}

const Controller = () => {
  useCheckRole([userTypes.Admin])
  return <TraceDetailPage />
}

export const TraceDetailPageRouteHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)

export const GroupDetailPageRouteHandler = TraceDetailPageRouteHandler
